/*
 * Sign in with Google or Facebook for Marketing (2026-11-01, owner: "just logging in and adding the page or Google
 * business profile, more scalable that way"). One sign in, then pick the ad account, Google profile and Facebook Page
 * from a list, instead of typing ids. A location uses its brand's sign in unless it signs in itself.
 */
import { useEffect, useState } from "react";
import type { SettingsPatch, SigninKind } from "@steward/shared";
import { useSettings, useUpdateSettings } from "../api/hooks";
import {
  useGbp,
  useGbpAttach,
  useGbpDetach,
  usePickGbp,
  useSignin,
  useSigninChoices,
  useSigninStart,
  useSignOut,
} from "../api/marketing";
import { useSession } from "../app/session";
import { Modal, useToast } from "../components/overlays";
import { Button, Card, Skeleton, StatusLabel } from "../components/parts";

const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");
const NAME: Record<SigninKind, string> = { google: "Google", meta: "Facebook" };
const TAB_KEY = "mk-signin-tab";

/** The Sign in with Google or Facebook buttons. Signed in already: one button to pick the accounts. */
export function SigninButtons({ kinds, tab }: { kinds: SigninKind[]; tab: string }) {
  const { company, can } = useSession();
  const s = useSignin(company.id);
  const start = useSigninStart();
  const toast = useToast();
  const [picking, setPicking] = useState(false);
  if (!can("settings.update") || !s.data) return null;
  const signedIn = kinds.filter((k) => s.data[k] && !s.data[k].signedOut);
  const out = kinds.filter((k) => !s.data[k] || s.data[k].signedOut);
  // Google or Facebook took the sign in back (an app in Testing does it after 7 days): say so, right by the button.
  const gone = kinds.filter((k) => s.data[k]?.signedOut);
  const go = (kind: SigninKind) =>
    start.mutate(kind, {
      onSuccess: (r) => {
        try {
          sessionStorage.setItem(TAB_KEY, tab);
        } catch {
          // No storage: the person comes back to the overview instead.
        }
        window.location.assign(r.url);
      },
      onError: (e) => toast(errText(e), { kind: "error" }),
    });
  return (
    <span className="mk-connect-actions">
      {signedIn.length ? (
        <Button type="secondary" size="small" onClick={() => setPicking(true)}>
          Pick accounts
        </Button>
      ) : null}
      {gone.map((k) => (
        <StatusLabel key={`gone-${k}`} status="critical">
          {NAME[k]} signed you out
        </StatusLabel>
      ))}
      {out.map((k) => (
        <Button
          key={k}
          type={gone.includes(k) ? "primary" : signedIn.length ? "tertiary" : "secondary"}
          size="small"
          loading={start.isPending && start.variables === k}
          onClick={() => go(k)}
        >
          {gone.includes(k) ? `Sign in with ${NAME[k]} again` : `Sign in with ${NAME[k]}`}
        </Button>
      ))}
      <SigninPicker open={picking} onClose={() => setPicking(false)} />
    </span>
  );
}

/** Tabs that only work once someone signs in, and what signing in gets them (owner, 2026-11-02: "make sure sign in is
 *  required to get started on them"). Any one of the kinds is enough. */
const GATE: Partial<Record<string, { kinds: SigninKind[]; what: string }>> = {
  profile: {
    kinds: ["google"],
    what: "Sign in with the Google account that runs your business profile. Then pick your listing to edit it, read every review and reply.",
  },
  ranks: {
    kinds: ["google"],
    what: "Sign in with a Google account that is a user in Search Console to see which searches bring people to your website.",
  },
  ads: {
    kinds: ["google", "meta"],
    what: "Sign in with Google or Facebook and pick your ad account to see what you spend and what it brings in.",
  },
  social: {
    kinds: ["google", "meta"],
    what: "Sign in with Google or Facebook and pick your profile or Page, so posts go out there.",
  },
};

/** Shows the tab only once a sign in it needs is in place (its own or the brand's above); until then one card to sign in. */
export function SigninGate({ tab, children }: { tab: string; children: React.ReactNode }) {
  const { company, can } = useSession();
  const s = useSignin(company.id);
  const gate = GATE[tab];
  if (!gate) return <>{children}</>;
  if (!s.data) return <Skeleton rows={3} />;
  const data = s.data;
  if (gate.kinds.some((k) => data[k] && !data[k].signedOut)) return <>{children}</>;
  const ready = gate.kinds.filter((k) => data.ready[k]);
  return (
    <Card title="Sign in to get started">
      <div className="stack">
        <p>{gate.what}</p>
        {!ready.length ? (
          <p className="muted">Head office adds the sign-in keys first, in Settings under Connected services.</p>
        ) : can("settings.update") ? (
          <div>
            <SigninButtons kinds={ready} tab={tab} />
          </div>
        ) : (
          <p className="muted">Ask the owner to sign in here.</p>
        )}
      </div>
    </Card>
  );
}

/** Back from Google or Facebook: says how it went, opens the picker when it worked, and goes back to the tab. */
export function SigninReturn({ onTab }: { onTab: (t: string) => void }) {
  const toast = useToast();
  const [picking, setPicking] = useState(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const how = q.get("signin");
    if (!how) return;
    const who = NAME[q.get("with") === "meta" ? "meta" : "google"];
    let tab: string | null = null;
    try {
      tab = sessionStorage.getItem(TAB_KEY);
      sessionStorage.removeItem(TAB_KEY);
    } catch {
      tab = null;
    }
    window.history.replaceState(null, "", window.location.pathname + window.location.hash);
    if (tab) onTab(tab);
    if (how === "done") {
      toast(`Signed in with ${who}. Pick your accounts.`);
      setPicking(true);
    } else if (how === "cancelled") toast(`${who} sign in was stopped. Nothing changed.`);
    else toast(`${who} sign in did not work. Try again.`, { kind: "error" });
    // Runs once, when the page opens.
  }, []);
  return <SigninPicker open={picking} onClose={() => setPicking(false)} />;
}

/** One account to turn on or off. Several in a section can be in use at once (owner, 2026-11-02). */
function Pick({
  name,
  line,
  inUse,
  busy,
  onPick,
  onDrop,
}: {
  name: string;
  line: string;
  inUse: boolean;
  busy: boolean;
  onPick: () => void;
  onDrop: () => void;
}) {
  return (
    <li className="setting-row mk-connect-row">
      <span className="what">
        <b>
          {name || "No name"} {inUse ? <StatusLabel status="success">In use now</StatusLabel> : null}
        </b>
        <span>{line}</span>
      </span>
      {inUse ? (
        <Button type="tertiary" size="small" loading={busy} onClick={onDrop}>
          Stop using
        </Button>
      ) : (
        <Button type="secondary" size="small" loading={busy} onClick={onPick}>
          Use this
        </Button>
      )}
    </li>
  );
}

const plain = (id: string) => id.replace(/\D/g, "");

/**
 * The accounts in use after turning one on or off, first one first. The first stays in its own setting (what Settings,
 * Marketing types in) and the rest go in the More list, so taking off the first moves the next one up.
 */
function toggled<T extends { id: string }>(now: T[], item: T, on: boolean): T[] {
  const rest = now.filter((x) => plain(x.id) && plain(x.id) !== plain(item.id));
  return on ? [...rest, item] : rest;
}

function Section({ title, empty, children }: { title: string; empty: string; children: React.ReactNode[] }) {
  return (
    <>
      <h3 className="mk-pick-title">{title}</h3>
      {children.length ? <ul className="auto-list">{children}</ul> : <p className="muted">{empty}</p>}
    </>
  );
}

/**
 * The ad accounts, Google profiles and Facebook Pages the sign in reaches. Each can be turned on or off, several at once
 * (owner, 2026-11-02: "allow multiple to be clicked"): ad spend adds up, posts go to every Page, and each Google profile
 * is listed on this company (the first one is the main listing for reviews and map rank).
 */
export function SigninPicker({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { company } = useSession();
  const s = useSignin(company.id);
  const choices = useSigninChoices(company.id, open);
  const settings = useSettings(company.id).data?.settings.marketing;
  const gbp = useGbp(company.id);
  const save = useUpdateSettings();
  const pickGbp = usePickGbp();
  const attach = useGbpAttach();
  const detach = useGbpDetach();
  const signOut = useSignOut();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const put = (key: string, patch: SettingsPatch["marketing"], word: string) => {
    setBusy(key);
    save.mutate(
      { marketing: patch },
      {
        onSuccess: () => toast(word),
        onError: (e) => toast(errText(e), { kind: "error" }),
        onSettled: () => setBusy(null),
      },
    );
  };
  const done = (word: string) => ({
    onSuccess: () => toast(word),
    onError: (e: unknown) => toast(errText(e), { kind: "error" }),
    onSettled: () => setBusy(null),
  });
  const g = choices.data?.google;
  const m = choices.data?.meta;
  const adsNow = settings
    ? [{ id: settings.googleAdsId, managerId: settings.googleAdsManagerId }, ...settings.googleAdsMore]
    : [];
  const metaNow = settings ? [settings.metaAdAccountId, ...settings.metaAdAccountsMore].map((id) => ({ id })) : [];
  const pagesNow = settings ? [settings.facebookPageId, ...settings.facebookPagesMore].map((id) => ({ id })) : [];
  const has = (now: { id: string }[], id: string) => now.some((x) => plain(x.id) && plain(x.id) === plain(id));
  const ads = (a: { id: string; managerId: string }, on: boolean) => {
    const list = toggled(adsNow, { id: a.id, managerId: a.managerId }, on);
    put(
      `g${a.id}`,
      {
        googleAdsId: list[0]?.id ?? "",
        googleAdsManagerId: list[0]?.managerId ?? "",
        googleAdsMore: list.slice(1),
      },
      on ? "Google Ads account added" : "Google Ads account taken off",
    );
  };
  const metaAds = (id: string, on: boolean) => {
    const list = toggled(metaNow, { id }, on).map((x) => plain(x.id));
    put(
      `m${id}`,
      { metaAdAccountId: list[0] ?? "", metaAdAccountsMore: list.slice(1) },
      on ? "Meta ad account added" : "Meta ad account taken off",
    );
  };
  const pages = (id: string, on: boolean) => {
    const list = toggled(pagesNow, { id }, on).map((x) => plain(x.id));
    put(
      `p${id}`,
      { facebookPageId: list[0] ?? "", facebookPagesMore: list.slice(1) },
      on ? "Facebook Page added" : "Facebook Page taken off",
    );
  };
  // The first Google profile becomes the main listing (it reads reviews right away); more are added beside it.
  const anyProfile = !!gbp.data?.profile || !!g?.profiles.some((p) => p.onHere);
  const addProfile = (p: { placeId: string; location: string }) => {
    setBusy(`b${p.placeId}`);
    if (!anyProfile || !p.location) pickGbp.mutate(p.placeId, done("Google profile saved"));
    else attach.mutate({ name: p.location, companyId: company.id, subLocation: null }, done("Google profile added"));
  };
  const dropProfile = (p: { placeId: string; onHere: string | null }) => {
    if (!p.onHere) return;
    setBusy(`b${p.placeId}`);
    detach.mutate(p.onHere, done("Google profile taken off"));
  };
  return (
    <Modal title="Pick your accounts" open={open} onClose={onClose}>
      {choices.isError ? (
        <p className="banner error">{errText(choices.error)}</p>
      ) : !choices.data || !settings ? (
        <Skeleton rows={4} />
      ) : !g && !m ? (
        <p className="muted">Sign in with Google or Facebook first.</p>
      ) : (
        <div className="stack">
          {g ? (
            <>
              <p className="muted">
                Google: {s.data?.google?.who}
                {s.data?.google && !s.data.google.own ? ", from the brand above" : ""}.{" "}
                {s.data?.google?.own ? (
                  <button className="link" type="button" onClick={() => signOut.mutate("google")}>
                    Sign out
                  </button>
                ) : null}
              </p>
              {g.error ? <p className="banner warning">{g.error}</p> : null}
              <Section title="Google business profile" empty="This Google account manages no business profiles.">
                {g.profiles.map((p) => (
                  <Pick
                    key={p.placeId}
                    name={p.name}
                    line={p.address || "No address on Google"}
                    inUse={!!p.onHere}
                    busy={busy === `b${p.placeId}`}
                    onPick={() => addProfile(p)}
                    onDrop={() => dropProfile(p)}
                  />
                ))}
              </Section>
              <Section title="Google Ads account" empty="This Google account has no Google Ads accounts.">
                {g.ads.map((a) => (
                  <Pick
                    key={a.id}
                    name={a.name}
                    line={a.managerId ? `${a.id}, under manager ${a.managerId}` : a.id}
                    inUse={has(adsNow, a.id)}
                    busy={busy === `g${a.id}`}
                    onPick={() => ads(a, true)}
                    onDrop={() => ads(a, false)}
                  />
                ))}
              </Section>
            </>
          ) : null}
          {m ? (
            <>
              <p className="muted">
                Facebook: {s.data?.meta?.who}
                {s.data?.meta && !s.data.meta.own ? ", from the brand above" : ""}.
                {s.data?.meta?.until ? ` Sign in again by ${new Date(s.data.meta.until).toLocaleDateString()}.` : ""}{" "}
                {s.data?.meta?.own ? (
                  <button className="link" type="button" onClick={() => signOut.mutate("meta")}>
                    Sign out
                  </button>
                ) : null}
              </p>
              {m.error ? <p className="banner warning">{m.error}</p> : null}
              <Section title="Facebook Page" empty="This Facebook account runs no Pages.">
                {m.pages.map((p) => (
                  <Pick
                    key={p.id}
                    name={p.name}
                    line={p.instagram ? "Instagram is linked, so posts go there too." : "No Instagram linked to this Page."}
                    inUse={has(pagesNow, p.id)}
                    busy={busy === `p${p.id}`}
                    onPick={() => pages(p.id, true)}
                    onDrop={() => pages(p.id, false)}
                  />
                ))}
              </Section>
              <Section title="Meta ad account" empty="This Facebook account has no ad accounts.">
                {m.adAccounts.map((a) => (
                  <Pick
                    key={a.id}
                    name={a.name}
                    line={`act_${a.id}`}
                    inUse={has(metaNow, a.id)}
                    busy={busy === `m${a.id}`}
                    onPick={() => metaAds(a.id, true)}
                    onDrop={() => metaAds(a.id, false)}
                  />
                ))}
              </Section>
            </>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
